(() => {
  if (window.__ME_PRODUCTION_LIGHTBOX_V735__) return;
  window.__ME_PRODUCTION_LIGHTBOX_V735__ = true;

  let modal;
  let image;
  let stage;
  let zoomLabel;
  let downloadButton;
  let currentObjectUrl = '';
  let previousFocus = null;
  let scale = 1;
  let offsetX = 0;
  let offsetY = 0;
  let dragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let startOffsetX = 0;
  let startOffsetY = 0;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  async function createPngObjectUrl(url) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(`Preview image failed (${response.status})`);
    const blob = await response.blob();
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d', { alpha: true });
    context.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    const pngBlob = await new Promise((resolve, reject) => {
      canvas.toBlob((result) => result ? resolve(result) : reject(new Error('PNG conversion failed.')), 'image/png', 1);
    });
    return URL.createObjectURL(pngBlob);
  }

  function updateTransform() {
    image.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;
    zoomLabel.textContent = `${Math.round(scale * 100)}%`;
    stage.classList.toggle('is-zoomed', scale > 1.01);
  }

  function resetView() {
    scale = 1;
    offsetX = 0;
    offsetY = 0;
    updateTransform();
  }

  function setScale(nextScale) {
    scale = clamp(nextScale, 0.5, 4);
    if (scale <= 1) {
      offsetX = 0;
      offsetY = 0;
    }
    updateTransform();
  }

  function close() {
    if (!modal?.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('me-lightbox-open');
    resetView();
    if (currentObjectUrl) {
      URL.revokeObjectURL(currentObjectUrl);
      currentObjectUrl = '';
    }
    image.removeAttribute('src');
    previousFocus?.focus?.();
    previousFocus = null;
  }

  function ensureModal() {
    if (modal) return;
    modal = document.createElement('div');
    modal.className = 'me-production-lightbox';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="me-production-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Production mockup viewer">
        <div class="me-production-lightbox__toolbar">
          <strong data-me-lightbox-title>Production mockup</strong>
          <button type="button" class="me-production-lightbox__close" data-me-lightbox-close aria-label="Close preview">×</button>
        </div>
        <div class="me-production-lightbox__stage" data-me-lightbox-stage>
          <div class="me-production-lightbox__loading" data-me-lightbox-loading>Preparing PNG preview…</div>
          <img alt="" draggable="false" data-me-lightbox-image>
        </div>
        <div class="me-production-lightbox__controls" role="group" aria-label="Preview zoom and download controls">
          <button type="button" data-me-lightbox-zoom-out aria-label="Zoom out">−</button>
          <button type="button" class="me-production-lightbox__zoom-label" data-me-lightbox-reset aria-label="Reset zoom to 100 percent">100%</button>
          <button type="button" data-me-lightbox-zoom-in aria-label="Zoom in">+</button>
          <button type="button" class="me-production-lightbox__download" data-me-lightbox-download>Download PNG</button>
        </div>
      </div>`;
    document.body.appendChild(modal);
    image = modal.querySelector('[data-me-lightbox-image]');
    stage = modal.querySelector('[data-me-lightbox-stage]');
    zoomLabel = modal.querySelector('[data-me-lightbox-reset]');
    downloadButton = modal.querySelector('[data-me-lightbox-download]');

    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target.closest('[data-me-lightbox-close]')) close();
      if (event.target.closest('[data-me-lightbox-zoom-in]')) setScale(scale + 0.25);
      if (event.target.closest('[data-me-lightbox-zoom-out]')) setScale(scale - 0.25);
      if (event.target.closest('[data-me-lightbox-reset]')) resetView();
    });

    stage.addEventListener('wheel', (event) => {
      event.preventDefault();
      setScale(scale + (event.deltaY < 0 ? 0.15 : -0.15));
    }, { passive: false });

    stage.addEventListener('pointerdown', (event) => {
      if (scale <= 1) return;
      dragging = true;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      startOffsetX = offsetX;
      startOffsetY = offsetY;
      stage.setPointerCapture(event.pointerId);
    });
    stage.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      offsetX = startOffsetX + event.clientX - dragStartX;
      offsetY = startOffsetY + event.clientY - dragStartY;
      updateTransform();
    });
    const stopDrag = () => { dragging = false; };
    stage.addEventListener('pointerup', stopDrag);
    stage.addEventListener('pointercancel', stopDrag);
  }

  async function open(trigger) {
    ensureModal();
    const loading = modal.querySelector('[data-me-lightbox-loading]');
    const title = modal.querySelector('[data-me-lightbox-title]');
    const url = trigger.dataset.url;
    const filename = trigger.dataset.filename || 'production-mockup.png';
    title.textContent = trigger.dataset.label || 'Production mockup';
    previousFocus = document.activeElement;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('me-lightbox-open');
    loading.hidden = false;
    image.hidden = true;
    resetView();
    requestAnimationFrame(() => modal.querySelector('[data-me-lightbox-close]')?.focus());

    try {
      currentObjectUrl = await createPngObjectUrl(url);
      image.src = currentObjectUrl;
      image.alt = title.textContent;
      image.hidden = false;
      loading.hidden = true;
      downloadButton.onclick = () => {
        const link = document.createElement('a');
        link.href = currentObjectUrl;
        link.download = filename.endsWith('.png') ? filename : `${filename}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      };
    } catch (error) {
      console.error('[Moments v7.3.5] Production lightbox failed.', error);
      loading.textContent = 'Preview could not be prepared. Use Download PNG instead.';
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-me-production-lightbox]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
})();
